import React, { useContext, useState } from 'react'
import Typography from '@mui/material/Typography'
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
} from '@mui/material'
import AnDateTimePicker from '../../../../../components/DateTime/DateTimePicker'
import S3DocumentUploadDisplay from '../../../../../components/images/S3DocumentUploadDisplay'
import API from '../../../../../services/API'
import { VehicleContext } from '../../SingleVehicle'
import { GlobalContext, TenantSettingsContext } from '../../../../../App'
import moment from 'moment'
import AnDateField from '../../../../../components/DateTime/DateField'
import TripleCheck from '../../../../../components/Switch/TripleCheck'
import { Close, Delete } from '@mui/icons-material'
import AttachmentIcon from '@mui/icons-material/Attachment'
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
} from '@mui/material'

const AddNewMOT = ({ onClose, existingMOT }) => {
  const [date, setDate] = useState(existingMOT?.date ?? null)
  const [mileage, setMileage] = useState(existingMOT?.mileage ?? '')
  const [pass, setPass] = useState(existingMOT?.pass ?? null)
  const [testLocation, setTestLocation] = useState(
    existingMOT?.testLocation ?? '',
  )
  const [testNumber, setTestNumber] = useState(existingMOT?.testNumber ?? '')
  const [advisories, setAdvisories] = useState(existingMOT?.advisories ?? '')
  const [cost, setCost] = useState(existingMOT?.cost ?? '')
  const [existingDocumentsToDelete, setExistingDocumentsToDelete] = useState([])
  const [openConfirmDeleteDocument, setOpenConfirmDeleteDocument] =
    useState(null)
  const [files, setFiles] = useState([])

  const { loadVehicle, vehicle } = useContext(VehicleContext)
  const { setToast } = useContext(GlobalContext)
  const { orgSettings } = useContext(TenantSettingsContext).orgConfig

  const handleSubmit = async (e) => {
    e.preventDefault()
    let s3Keys = [
      ...(existingMOT?.documents ?? []).filter(
        (x) => !existingDocumentsToDelete.includes(x),
      ),
    ]

    const pushToDb = async () => {
      const payload = {
        mot: {
          date,
          mileage,
          pass,
          testLocation,
          testNumber,
          advisories,
          documents: s3Keys,
          cost: cost === '' ? undefined : parseFloat(cost),
        },
        ...(existingMOT?._id ? { motId: existingMOT._id } : {}),
      }
      await API.put('/fleet/' + vehicle._id, payload).then((res) => {
        if (res.status === 200) {
          setToast({ message: `MOT ${existingMOT ? 'Updated' : 'Added'}` })
          loadVehicle()
          onClose()
        } else {
          setToast({
            message: `Unable to ${existingMOT ? 'update' : 'add'} MOT. Please try again later`,
            severity: 'error',
          })
        }
      })
    }

    if (files?.length) {
      const uploadedKeys = await Promise.all(
        Array.from(files).map(async (file) => {
          const res = await API.post('/gets3uploadurl', {
            folder: 'vehicles/documents',
            extension: file.type.split('/')[1],
          })
          await API.put(res.data.url, file, {
            headers: { 'Content-Type': file.type },
          })
          return res.data.key
        }),
      )
      s3Keys = [...s3Keys, ...uploadedKeys]
    }

    await pushToDb()

    if (Array.from(existingDocumentsToDelete).length) {
      Array.from(existingDocumentsToDelete).forEach(async (file) => {
        await API.post('/deletes3object', {
          key: file,
        })
      })
    }
  }

  return (
    <Grid
      container
      spacing={2}
      component="form"
      onSubmit={handleSubmit}
      padding={1}
    >
      <Grid item xs={12}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="title2">
            {existingMOT
              ? `Edit MOT for ${vehicle.callSign}`
              : `Add MOT to ${vehicle.callSign}`}
          </Typography>
          <IconButton size="small" onClick={onClose} aria-label="Close">
            <Close />
          </IconButton>
        </Stack>
        <Divider sx={{ mt: 1 }} />
      </Grid>
      <Grid item xs={12} lg={4}>
        <AnDateField
          date={date}
          onChange={(newDate) => setDate(newDate)}
          label="Date"
          fullWidth
        />
      </Grid>
      <Grid item xs={12} lg={4}>
        <TextField
          value={mileage}
          onChange={(e) => setMileage(e.target.value)}
          label="Mileage"
          fullWidth
          required
          autoComplete="off"
        />
      </Grid>
      <Grid item xs={12} lg={4}>
        <FormControlLabel
          label="Pass"
          control={
            <TripleCheck
              value={pass}
              onChange={(newValue) => setPass(newValue)}
            />
          }
        />
      </Grid>
      <Grid item xs={12} lg={4}>
        <TextField
          value={testLocation}
          onChange={(e) => setTestLocation(e.target.value)}
          label="Test Location"
          autoComplete="off"
          required
          fullWidth
        />
      </Grid>
      <Grid item xs={12} lg={4}>
        <TextField
          value={testNumber}
          onChange={(e) => setTestNumber(e.target.value)}
          label="Test Number"
          fullWidth
          required
          autoComplete="off"
        />
      </Grid>
      <Grid item xs={12} lg={4}>
        <TextField
          type="number"
          value={cost}
          onChange={(e) => setCost(e.target.value)}
          label="Cost"
          autoComplete="off"
          fullWidth
          InputProps={{
            inputProps: { min: 0 },
            startAdornment: (
              <InputAdornment position="start">
                {orgSettings?.currency ?? '£'}
              </InputAdornment>
            ),
          }}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          value={advisories}
          onChange={(e) => setAdvisories(e.target.value)}
          multiline
          minRows={3}
          label="Advisories"
          autoComplete="off"
          fullWidth
        />
      </Grid>
      {existingMOT?.documents?.length ? (
        <Grid item xs={12}>
          <Stack gap={1}>
            <Typography variant="subtitle2" fontWeight={700}>
              Existing Documents
            </Typography>
            {(existingMOT.documents ?? [])
              .filter((x) => !existingDocumentsToDelete.includes(x))
              .map((x, i) => {
                const fileTitle = () => {
                  const fileType =
                    typeof x === 'string' ? x.split('.').slice(-1)[0] : null
                  if (['jpeg', 'jpg', 'png', 'gif'].includes(fileType))
                    return 'Image'
                  if (fileType === 'pdf') return 'PDF'
                  return 'File'
                }
                return (
                  <Box
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                    }}
                    key={i}
                  >
                    <Stack
                      direction="row"
                      gap={1}
                      justifyContent="space-between"
                      alignItems="center"
                      paddingX={1}
                      paddingY={0.5}
                    >
                      <Stack direction="row" gap={1} alignItems="center">
                        <AttachmentIcon fontSize="small" color="action" />
                        <Typography variant="body2">{fileTitle()}</Typography>
                      </Stack>
                      <IconButton
                        size="small"
                        onClick={() => setOpenConfirmDeleteDocument(x)}
                      >
                        <Delete fontSize="small" color="anRed" />
                      </IconButton>
                    </Stack>
                  </Box>
                )
              })}
          </Stack>
          <Dialog
            open={!!openConfirmDeleteDocument}
            onClose={() => setOpenConfirmDeleteDocument(null)}
          >
            <DialogTitle sx={{ fontWeight: 800 }}>
              Delete this document?
            </DialogTitle>
            <DialogContent>
              <DialogContentText>
                The file will be permanently removed when you save. This
                cannot be undone.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button
                color="inherit"
                onClick={() => setOpenConfirmDeleteDocument(null)}
              >
                Cancel
              </Button>
              <Button
                color="error"
                variant="contained"
                onClick={() => {
                  setExistingDocumentsToDelete((prev) =>
                    prev.includes(openConfirmDeleteDocument)
                      ? prev
                      : [...prev, openConfirmDeleteDocument],
                  )
                  setOpenConfirmDeleteDocument(null)
                }}
              >
                Delete
              </Button>
            </DialogActions>
          </Dialog>
        </Grid>
      ) : (
        ''
      )}
      <Grid item xs={12}>
        <TextField
          type="file"
          defaultValue={files}
          inputProps={{
            accept: ['application/pdf', '.doc', '.docx', 'image/*'],
            multiple: true,
          }}
          onChange={(e) => setFiles(e.target.files)}
          fullWidth
          label="Attachments"
          InputLabelProps={{
            shrink: true,
          }}
        />
      </Grid>
      <Grid item xs={12}>
        <Stack direction="row" justifyContent="flex-end" gap={1}>
          <Button color="inherit" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={!moment(date).isValid() || pass === null}
          >
            {existingMOT ? 'Save Changes' : 'Add MOT'}
          </Button>
        </Stack>
      </Grid>
    </Grid>
  )
}

export default AddNewMOT
